<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import Block from '@/views/lucahub/Block.vue';
import { useStore } from 'vuex';
import { $api } from '@/services';

const store = useStore();
const props = defineProps({
	othersInfo: {
		type: Object,
		required: true,
	},
	id: {
		type: String,
		required: true,
	},
	mainService: {
		type: String,
		required: true,
	},
});

const data = ref(JSON.parse(JSON.stringify(props.othersInfo)));
const emit = defineEmits(['close', 'refresh']);

const uploadFileToS3 = async (action, signature, file) => {
	const formData = new FormData();
	formData.append('AWSAccessKeyId', signature['AWSAccessKeyId'] || '');
	formData.append('key', signature.key || '');
	formData.append('acl', signature.acl || '');
	formData.append('Content-Type', signature['Content-Type'] || '');
	formData.append('x-amz-signature', signature.signature || '');
	formData.append('Signature', signature.signature || '');
	formData.append('Policy', signature.policy || '');
	formData.append(
		'success_action_status',
		signature.success_action_status || ''
	);
	formData.append('file', file);

	const { data: s3Response } = await $api.file.uploadToS3(action, formData);
	const keyData = s3Response.toString().match('<Key>(.*)</Key>');
	const urlData = s3Response.toString().match('<Location>(.*)</Location>');
	return {
		asset_key: keyData && keyData.length ? keyData[1] : '',
		type: file.type,
		name: file.name,
		url: urlData && urlData.length ? urlData[1] : '',
	};
};

const getSignatures = async (listImage) => {
	const fileList = listImage;
	const {
		data: { data: signatures },
	} = await $api.file.getSignatures({
		mime_types: fileList.map((f) => f.type),
		prefix: 'lucahub',
	});
	if (!signatures?.length) return [];

	const uploaded = [];
	for (let i = 0; i < signatures.length; i++) {
		const result = await uploadFileToS3(
			signatures[i].action,
			{
				...signatures[i],
				AWSAccessKeyId: signatures[0]['AWSAccessKeyId'],
			},
			fileList[i]
		);
		uploaded.push(result);
	}
	return uploaded;
};

const uploadSingleImageField = async (field) => {
	if (!isUploadableImage(data.value[field])) return;
	const uploaded = await getSignatures([data.value[field]]);
	if (uploaded.length) {
		data.value[field] = uploaded[0];
	}
};

const uploadAdditionalImages = async () => {
	const images = Array.isArray(data.value.additional_url_images)
		? data.value.additional_url_images
		: [];
	const filesToUpload = images.filter(isUploadableImage);
	if (!filesToUpload.length) return;

	const uploaded = await getSignatures(filesToUpload);
	let uploadIndex = 0;
	data.value.additional_url_images = images.map((img) => {
		if (isUploadableImage(img)) {
			return uploaded[uploadIndex++];
		}
		return img;
	});
};

const IMAGE_FIELDS = [
	'therapy_model_url_image',
	'additional_utilities_and_services_url_image',
	'target_patients_or_clients_url_image',
	'learning_program_url_image',
	'consultation_program_url_image',
];

const isUploadableImage = (image) =>
	image !== null && typeof image === 'object' && image instanceof File;

const normalizeImagePayload = (value) => {
	if (typeof value === 'string') return value;
	if (value && typeof value === 'object' && value.asset_key) {
		return {
			asset_key: value.asset_key,
			mime_type: value.type || value.mime_type,
			name: value.name,
		};
	}
	return value;
};

const buildPayload = () => {
	const payload = { ...data.value };
	IMAGE_FIELDS.forEach((field) => {
		payload[field] = normalizeImagePayload(payload[field]);
	});

	const additionalImages = Array.isArray(payload.additional_url_images)
		? payload.additional_url_images
		: [];
	payload.additional_url_images = additionalImages
		.map(normalizeImagePayload)
		.filter(Boolean);

	return payload;
};

const updateOthersInfo = async () => {
	try {
		await uploadSingleImageField('therapy_model_url_image');
		await uploadSingleImageField(
			'additional_utilities_and_services_url_image'
		);
		await uploadSingleImageField('target_patients_or_clients_url_image');
		await uploadSingleImageField('learning_program_url_image');
		await uploadSingleImageField('consultation_program_url_image');
		await uploadAdditionalImages();

		const res = await $api.org.updateOthersInfo(props.id, buildPayload());
		if (res) {
			emit('refresh');
			store.dispatch('setAlertMessage', {
				message: 'Cập nhật thông tin thành công',
				type: 'success',
			});
		}
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
</script>
<template>
	<div class="flex items-center justify-between mb-10">
		<div class="flex items-center gap-2">
			<img
				class="w-4 h-4 cursor-pointer"
				style="transform: rotate(180deg)"
				src="@/assets/images/icons/arrow-right.png"
				alt=""
				@click="$emit('close')"
			/>
			<div class="text-neutral-700 text-4xl font-bold">
				Quản lý nội dung Luca Hub
			</div>
		</div>
		<button class="button-p text-center w-max" @click="updateOthersInfo">
			Lưu thay đổi
		</button>
	</div>
	<div class="bg-white rounded-lg shadow-sm mb-10">
		<div class="divine-line py-4 font-semibold text-neutral-600">
			<div class="px-6">Thông tin giới thiệu</div>
		</div>
		<div class="px-6 py-4">
			<div class="flex gap-4">
				<div
					class="text-xs font-semibold text-neutral-400 uppercase w-36 flex-shrink-0"
				>
					Mô tả trung tâm
				</div>
				<textarea
					rows="4"
					class="w-full"
					placeholder="Nhập nội dung giới thiệu sẽ được hiển thị trên trang Luca Hub của trung tâm"
					v-model="data.description"
				></textarea>
			</div>
		</div>
	</div>
	<Block
		:title="'Mô hình trị liệu'"
		v-model:description="data.therapy_model_description"
		v-model:image="data.therapy_model_url_image"
	/>
	<Block
		:title="'Tiện ích & dịch vụ bổ sung'"
		v-model:description="data.additional_utilities_and_services_description"
		v-model:image="data.additional_utilities_and_services_url_image"
	/>
	<Block
		:title="'Đối tượng can thiệp/thăm khám'"
		v-model:description="data.target_patients_or_clients_description"
		v-model:image="data.target_patients_or_clients_url_image"
	/>
	<Block
		v-if="
			props.mainService === 'intervention_facility' ||
			props.mainService === 'integrated_intervention_diagnostic_facility'
		"
		:title="'Chương trình học'"
		v-model:description="data.learning_program_description"
		v-model:image="data.learning_program_url_image"
	/>
	<Block
		v-if="
			props.mainService === 'diagnostic_evaluation' ||
			props.mainService === 'integrated_intervention_diagnostic_facility'
		"
		:title="'Chương trình thăm khám'"
		v-model:description="data.consultation_program_description"
		v-model:image="data.consultation_program_url_image"
	/>
	<Block
		v-if="
			props.mainService === 'diagnostic_evaluation' ||
			props.mainService === 'integrated_intervention_diagnostic_facility'
		"
		:title="'Hình ảnh bổ sung'"
		multiple
		:show-description="false"
		v-model:image="data.additional_url_images"
	/>
	<!-- <div class="bg-white rounded-lg shadow-sm mb-10">
		<div class="divine-line py-4 font-semibold text-neutral-600">
			<div class="px-6">Hình ảnh bổ sung</div>
		</div>
		<div class="px-6 py-4">
			<div class="flex gap-4 mb-6">
				<div
					class="text-xs font-semibold text-neutral-400 uppercase w-36"
				>
					Hình ảnh
				</div>
				<div v-if="data.therapy_model_url_image"></div>
				<div
					v-else
					class="w-full text-neutral-500 bg-white flex flex-col items-center justify-senter text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
					@click="openModelUpload('operating_license')"
				>
					<img
						src="@/assets/images/icons/cloud-upload-fill.png"
						alt=""
					/>
					<div class="font-semibold">Tải lên hình ảnh minh họa</div>
				</div>
			</div>
		</div>
	</div> -->
</template>
<style scoped>
.divine-line {
	border-bottom: 2px solid #faf5f1;
}
</style>